<script lang="ts">
  import Form from "../../components/Form.svelte";
  import RadioGroup from "../../components/RadioGroup.svelte";
  import SharepointFileInput from "../../components/SharepointFileInput.svelte";
  import Caret from "~icons/ph/caret-down";
  import Info from "~icons/ph/info";
  import CalendarCheck from "~icons/ph/calendar-check";
  import { domId } from "../../lib/dom";

  let {
    action,
    errors,
    clientFirstName,
    createUploadSessionUrl,
    uploadAttachmentExtensions,
  }: {
    action: string;
    errors: { [key: string]: string } | null;
    clientFirstName: string;
    createUploadSessionUrl: string;
    uploadAttachmentExtensions: string[];
  } = $props();

  let selectedOption = $state("yes");

  const closeDialogId = domId("close-dialog");
</script>

<Form method="patch" {action} class="ui-rhythm">
  <h2 class="ui-title">Upload will</h2>

  <p>
    If {clientFirstName} had a will, we’ll use it to ensure you’re their executor—the person appointed to handle
    their legal and financial matters once deceased.
  </p>

  <details class="ui-details">
    <summary>
      <Caret />How do I know if they had a will?
    </summary>

    <p>
      A will is a legal document that outlines a person’s wishes for the distribution of their estate after
      their death including money, property and possessions. It also typically appoints an executor to manage
      those affairs and will only take effect after death. To check if a will exists, we advise you to look
      through the deceased’s home for any personal documents or ask trusted relatives or friends. You can also
      use
      <a href="https://www.nationalwillregister.co.uk/" target="_blank" rel="noreferrer" class="link text-blue-600">
        The National Will Register
      </a>
      to check if a will was registered there or contact any law firms the deceased used to have contacts with.
    </p>
  </details>

  <fieldset class="ui-fieldset">
    <legend>Did {clientFirstName} have a will?</legend>

    <RadioGroup
      name="has_document"
      options={[
        ["Yes", "yes"],
        ["No", "no"],
        ["I’m not sure, and need time to find out", "not_sure"],
      ]}
      bind:group={selectedOption}
      required />
  </fieldset>

  <div class="ui-content-options mt-6">
    <fieldset class="ui-fieldset" class:invisible={selectedOption !== "yes"}>
      <legend>Upload will</legend>

      {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}

      <div>
        <p class="ui-alert ui-alert-info pb-4 -mb-2.5 mt-0">Please provide clear and legible photo(s) of the will. Add all pages before sending.</p>
        <SharepointFileInput
          name="will"
          accept={uploadAttachmentExtensions}
          {createUploadSessionUrl}
          multiple
          required={selectedOption === "yes"} />
      </div>

      {#if errors?.will}<div class="ui-error">{errors.will}</div>{/if}
    </fieldset>

    <div class:invisible={selectedOption !== "no"}>
      <div class="ui-alert ui-alert-info">
        <Info />
        <p>
          It’s common not to have a will: we’ll guide you through providing Letters of Administration next,
          another type of document that shows you’re able to act on their behalf.
        </p>
      </div>

      {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}
    </div>

    <div class:invisible={selectedOption !== "not_sure"}>
      <div class="ui-alert ui-alert-info">
        <CalendarCheck />
        <p>
          Take all the time you need, and we’ll check back in a month. Until then, feel free to use our resources above
          to find this document. When you find it, you can upload it here.
        </p>
      </div>

      {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}
    </div>
  </div>

  <div class="floating-footer">
    <button class="btn btn-ghost" form={closeDialogId}>Cancel</button>
    <button class="btn btn-primary" type="submit" data-viewer-mode>{selectedOption === "yes" ? "Send" : "Finish"}</button>
  </div>
</Form>

<form method="dialog" id={closeDialogId}></form>
